"use client";

// Общие пресентационные детали карточек команд/участников (вкладка «Команды»),
// вынесенные из макета «Страница турнира.dc.html», чтобы единообразно
// переиспользовать во всех гридах (классик / драфт / соло / арена).

import { getRiotRankImageUrl } from "@/shared/lib/rank-image";
import { useTranslations } from "next-intl";
import { RoleIcon } from "@/shared/ui/data-display/role-icon";

const TIER_SHORT: Record<string, string> = {
  iron: "I",
  bronze: "B",
  silver: "S",
  gold: "G",
  platinum: "P",
  emerald: "E",
  diamond: "D",
  master: "M",
  grandmaster: "GM",
  challenger: "C",
};

const ROMAN_TO_NUM: Record<string, string> = {
  i: "1",
  ii: "2",
  iii: "3",
  iv: "4",
  v: "5",
};

/** Короткий код ранга: «Platinum IV» → «P4», «Master» → «M». */
export function rankShortCode(rank?: string | null): string {
  if (!rank) return "";
  const tierMatch = rank
    .toLowerCase()
    .match(
      /grandmaster|challenger|master|iron|bronze|silver|gold|platinum|emerald|diamond/,
    );
  const tier = tierMatch?.[0] ?? "";
  const letter = TIER_SHORT[tier] ?? (tier ? tier[0]!.toUpperCase() : "");
  const hasDivision = !/master|grandmaster|challenger/.test(tier);
  if (!hasDivision) return letter;
  const divMatch = rank.match(/\b(IV|III|II|I|[1-4])\b/i);
  const div = divMatch
    ? (ROMAN_TO_NUM[divMatch[1]!.toLowerCase()] ?? divMatch[1])
    : "";
  return `${letter}${div}`;
}

/** Ранг-пилюля из макета: иконка ранга + короткий код. Пусто → null. */
export function RankPill({ rank }: { rank?: string | null }) {
  const icon = getRiotRankImageUrl(rank);
  const short = rankShortCode(rank);
  if (!rank || !icon) return null;
  return (
    <span
      title={rank}
      className="inline-flex w-[58px] flex-none items-center justify-center gap-1 rounded-full border border-border bg-muted px-1.5 py-[3px]"
    >
      <img
        src={icon}
        alt={rank}
        loading="lazy"
        decoding="async"
        className="h-[18px] w-[18px] flex-none object-contain"
      />
      {short ? (
        <span className="inline-flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-primary/15 px-1 text-[9px] font-bold leading-none text-foreground">
          {short}
        </span>
      ) : null}
    </span>
  );
}

/** Роль в квадратной плашке (из макета): иконка роли на secondary-фоне. */
export function MemberRoleSquare({
  role,
  title,
}: {
  role?: string | null;
  title?: string;
}) {
  if (!role) return null;
  return (
    <span
      title={title ?? role}
      className="inline-flex h-[22px] w-[22px] flex-none items-center justify-center rounded-md bg-secondary text-muted-foreground"
    >
      <RoleIcon role={role} size={14} />
    </span>
  );
}

/** Капитанская звезда (из макета). */
export function CaptainStar({ className }: { className?: string }) {
  const tUi = useTranslations("Ui");
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="none"
      className={className ?? "h-3 w-3 flex-none text-primary"}
      aria-label={tUi("teamCardBits.kapitan")}
    >
      <path d="M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.735H5.81a1 1 0 0 1-.957-.735L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z" />
    </svg>
  );
}
